import { FormEvent, useEffect, useState, useCallback } from "react";
import { IClothesResponse, IClothItem } from "../../../../types/index";
import { appAxiosInstance, clothesAxiosInstance } from "../../../../api";
import { toast } from "react-toastify";
import { Button } from "react-bootstrap";
import s from "./clothesList.module.css";
import { ClothesItemList } from "../ClothesItemList";
import { Link, useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { PaginationControl } from "react-bootstrap-pagination-control";
import { CustomSelector } from './CustomSelector/customSelector';
import { Loader } from "../../../../components/Loader/loader";

interface ICharItemFilter {
    charName: string;
    charValues: string | null;
}

interface IArchiveRequest {
    isHidden: boolean;
}

interface FiltersBySourceTags {
    onlyNotTagged: boolean;
    onlyAutoTagged: boolean;
}

interface IClothesRequestParams {
    currentPage: string;
    itemsPerPage: string;
    id: number | null;
    name?: string | null;
    categoryId?: string | null;
    manufacturer?: string | null;
    layer?: string | null;
    vendorCode?: string | null;
    isNewImport: boolean | null,
    isSegmentedClothes: boolean | null,
    isUpdateImport: boolean | null,
    sortColumn?: string | null;
    ourBrands: boolean | null;
    sortDirection?: string | null;
    characteristics: Array<ICharItemFilter>;
    fullMatch: boolean;
    gender: string | null;
    isHidden: boolean;
    isMissingClothes: boolean;
    filtersForTags: FiltersBySourceTags;
    importStatus?: 'new' | 'changed' | null;
    onlyOurBrands?: boolean;
}

type TabType = 'new' | 'changed' | 'missing' | 'segmented' | 'brands' | 'all';

const LAYERS = [
    { name: "ABOVE_THE_WAIST_FIRST_LAYER", localizedName: "Плечевой первый слой" },
    { name: "ABOVE_THE_WAIST_SECOND_LAYER", localizedName: "Плечевой второй слой" },
    { name: "OUTERWEAR", localizedName: "Плечевые верхняя одежда" },
    { name: "PANTS", localizedName: "Поясные" },
    { name: "SHOES", localizedName: "Обувь" },
    { name: "BAG", localizedName: "Сумка" },
    { name: "ACCESSORIES", localizedName: "Аксессуары" }
];

const TABS: { key: TabType; label: string }[] = [
    { key: 'all', label: 'Вся одежда' },
    { key: 'brands', label: 'Наши бренды' },
    { key: 'new', label: 'Новая импортированная' },
    { key: 'changed', label: 'Изменённая в импорте' },
    { key: 'missing', label: 'Не актуальная одежда' },
    { key: 'segmented', label: 'Автоматически сегментированная' },
];

export function ClothesList() {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const location = useLocation();

    const currentPage = searchParams.get('page') || '1';
    const [currentPageInput, setCurrentPageInput] = useState('1');

    const [activeTab, setActiveTab] = useState<TabType>('all');

    const [clothes, setClothes] = useState<IClothItem[]>([]);
    const [allItems, setAllItems] = useState(0);
    const [itemPerPage, setItemPerPage] = useState(10);
    const [vendorCode, setVendorCode] = useState('');
    const [name, setName] = useState('');
    const [id, setId] = useState('');
    const [selectedLayer, setSelectedLayer] = useState('');
    const [manufacturer, setManufacturer] = useState('');
    const [sortColumn, setSortColumn] = useState('');
    const [sortDirection, setSortDirection] = useState<string | null>(null);
    const [characteristics, setCharacteristics] = useState<Array<ICharItemFilter>>([{ charName: '', charValues: '' }]);
    const [fullMatch, setFullMatch] = useState(false);
    const [isArchive, setIsArchive] = useState(false);
    const [genderFilter, setGenderFilter] = useState<'female' | 'male' | 'unisex' | 'all'>('all');
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [brands, setBrands] = useState<string[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [onlyNotTagged, setOnlyNotTagged] = useState(false);
    const [onlyAutoTagged, setOnlyAutoTagged] = useState(false);
    const [segmentedClothes, setSegmentedClothes] = useState(false);

    useEffect(() => {
        if (location.state?.searchId) {
            setId(location.state.searchId);
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.state, navigate, location.pathname]);

    useEffect(() => {
        if (location.state?.searchId && id === location.state.searchId && !isInitialLoad) {
            getClothes();
        }
    }, [id, location.state, isInitialLoad]);

    const handleArchiveToggle = (archiveState: boolean) => {
        setIsArchive(archiveState);
    };

    const handleFiltersBySourceTagsToggle = (value: string) => {
        if (value === "all") {
            setOnlyAutoTagged(false);
            setOnlyNotTagged(false);
        } else if (value === "auto") {
            setOnlyAutoTagged(true);
            setOnlyNotTagged(false);
        } else {
            setOnlyAutoTagged(false);
            setOnlyNotTagged(true);
        }
    }

    const getGenderValue = (filter: 'female' | 'male' | 'unisex' | 'all'): string | null => {
        switch (filter) {
            case 'female': return 'female';
            case 'male': return 'male';
            case 'unisex': return 'unisex';
            case 'all': return null;
            default: return null;
        }
    };

    const setCurrentPage = (page: string) => {
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.set('page', page);
        setSearchParams(newSearchParams);
    };

    const fullMatchChange = () => {
        setFullMatch(!fullMatch);
    };

    const addCharacteristic = () => {
        setCharacteristics([...characteristics, { charName: '', charValues: '' }]);
    };

    const paginate = (pageNumbers: number) => {
        setCurrentPage(pageNumbers.toString());
    }

    const charNameChange = (index: number, value: string) => {
        const updatedCharacteristics = [...characteristics];
        updatedCharacteristics[index].charName = value;
        setCharacteristics(updatedCharacteristics);
    };

    const charValuesChange = (index: number, value: string) => {
        const updatedCharacteristics = [...characteristics];
        updatedCharacteristics[index].charValues = value;
        setCharacteristics(updatedCharacteristics);
    };

    const getClothes = useCallback(async () => {
        setIsLoading(true);
        try {
            const processedCharacteristics = characteristics
                .filter(item => item.charName !== '')
                .map(item => ({
                    charName: item.charName,
                    charValues: item.charValues === "" ? null : item.charValues
                }));

            const ourCloth = activeTab === "brands" ? true : false;
            const isMissingCloth = activeTab === "missing" ? true : false;
            const isNewImport= activeTab === "new" ? true : null;
            const isUpdateImport = activeTab === "changed" ? true : null;
            const isSegmentedClothes = activeTab === "segmented" ? true : null;

            const params: IClothesRequestParams = {
                currentPage: currentPage,
                itemsPerPage: itemPerPage.toString(),
                id: Number(id) || null,
                name: name || null,
                categoryId: null,
                manufacturer: manufacturer || null,
                layer: selectedLayer ? (LAYERS.find(layer => layer.localizedName === selectedLayer)?.name) : null,
                vendorCode: vendorCode || null,
                sortColumn: sortColumn || "id",
                sortDirection: sortDirection || null,
                isNewImport: isNewImport,
                isUpdateImport: isUpdateImport,
                characteristics: processedCharacteristics,
                ourBrands: ourCloth,
                isSegmentedClothes: isSegmentedClothes,
                filtersForTags: {
                    onlyNotTagged: onlyNotTagged,
                    onlyAutoTagged: isSegmentedClothes != null ? isSegmentedClothes :onlyAutoTagged
                },
                fullMatch: fullMatch,
                gender: getGenderValue(genderFilter),
                isHidden: isArchive,
                isMissingClothes: isMissingCloth
            };

            const clothesListResponse = await clothesAxiosInstance.post<IClothesResponse>(
                `list`, params, { headers: { 'Content-Type': 'application/json' } }
            );

            const clothesResponse = clothesListResponse.data;

            if (clothesResponse.clothes.length) {
                setClothes(clothesResponse.clothes);
                setAllItems(clothesResponse.countClothes);
            } else {
                setClothes([]);
            }

        } catch (error) {
            setClothes([]);
            toast.error('Возникла ошибка при получении списка одежды');
        } finally {
            setIsLoading(false);
        }
    }, [currentPage, itemPerPage, id, name, selectedLayer, manufacturer, vendorCode, sortColumn, sortDirection,
        characteristics, fullMatch, genderFilter, isArchive, onlyAutoTagged, onlyNotTagged, activeTab]);

    const find = (event: FormEvent | React.KeyboardEvent) => {
        event.preventDefault();
        setCurrentPage('1');
        getClothes();
    }

    const changeSort = (column: string) => {
        if (sortColumn === column) {
            setSortDirection((prevDirection) => {
                if (prevDirection === 'asc') return 'desc';
                else if (prevDirection === 'desc') {
                    setSortColumn('');
                    return null;
                } else return 'asc';
            });
        } else {
            setSortColumn(column);
            setSortDirection('asc');
        }
    };

    const deleteMissingCloth = async (clothId: number) => {
        try {
            await clothesAxiosInstance.post(`/deletingMissingClothes/${clothId}`);
            getClothes();
        } catch (error) {
            toast.error('Ошибка при удалении из списка отсутствующих');
        }
    };

    const getBrands = async () => {
        try {

            const ourCloth = activeTab === "brands" ? true : false;

            const response = await clothesAxiosInstance.get<string[]>('getBrands', {
                params: { ourBrands: ourCloth }
            });
            if (response.data.length) {
                setBrands(response.data);
            } else {
                setBrands([]);
            }
        } catch (error) {
            toast.error('Ошибка при получении списка брендов');
            setBrands([]);
        }
    }

    const deleteCloth = async (clothId: number) => {
        try {
            const deleteClothResponse = await clothesAxiosInstance.delete<boolean>(
                `${clothId}`
            );
            const response = deleteClothResponse.data;

            if (response) {
                getClothes();
                toast.success('Одежда успешно удалена');
            } else {
                toast.error('Возникла ошибка при удалении одежды');
            }
        } catch (error) {
            toast.error('Возникла ошибка при получении списка одежды');
        }
    }

    const archiveCloth = async (clothId: number) => {
        try {
            const requestDto: IArchiveRequest = {
                isHidden: !isArchive
            };

            const archiveClothResponse = await clothesAxiosInstance.post<boolean>(
                `setHidden/${clothId}`, requestDto
            );
            const response = archiveClothResponse.data;

            if (response) {
                getClothes();
                toast.success('Одежда успешно перенесена в архив');
            } else {
                toast.error('Возникла ошибка при добавлении одежды в архив');
            }
        } catch (error) {
            toast.error('Возникла ошибка при получении списка одежды');
        }
    }

    const cleanForm = () => {
        setName('');
        setOnlyNotTagged(false);
        setOnlyAutoTagged(false);
        setSelectedLayer('');
        setManufacturer('');
        setCharacteristics([{ charName: '', charValues: '' }]);
        setFullMatch(false);
        setVendorCode('');
        setId('');
        setSortColumn('');
        setSortDirection(null);
        setCurrentPage('1');
        getClothes();
    }

    const pageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const pageValue = event.target.value;
        if (pageValue === '') {
        } else {
            const page = parseInt(pageValue, 10);
            if (!isNaN(page) && page >= 1 && page <= Math.ceil(allItems / itemPerPage)) {
                setCurrentPage(page.toString());
            }
        }
    };

    const handleTabChange = (tab: TabType) => {
        setActiveTab(tab);
        if (tab === 'segmented') {
            setSegmentedClothes(true);
        } else {
            setSegmentedClothes(false);
        }
        setCurrentPage('1');
    };

    useEffect(() => {
        getBrands();
    }, [activeTab]);

    useEffect(() => {
        if (isInitialLoad) {
            setIsInitialLoad(false);
            return;
        }
        getClothes();
        setCurrentPageInput(currentPage);
    }, [currentPage, sortColumn, sortDirection, isArchive, genderFilter, isInitialLoad, onlyAutoTagged,
        onlyNotTagged, activeTab]);

    useEffect(() => {
        if (!isInitialLoad) {
            setCurrentPage('1');
        }
    }, [isArchive, genderFilter, activeTab]);

    return (
        <>
            <div className={s.btnUp}>
                <Link to={'/create'}>
                    <Button variant="success" className={s.addingClothFormButton}>Добавить модель одежды</Button>
                </Link>
                <div className={s.switches}>
                    <div className={s.genderSelector}>
                        <div className={s.selector}>
                            <div
                                className={`${s.selectorItem} ${genderFilter === 'all' ? s.active : ''}`}
                                onClick={() => setGenderFilter('all')}
                            >
                                Все
                            </div>
                            <div
                                className={`${s.selectorItem} ${genderFilter === 'male' ? s.active : ''}`}
                                onClick={() => setGenderFilter('male')}
                            >
                                Мужской
                            </div>
                            <div
                                className={`${s.selectorItem} ${genderFilter === 'female' ? s.active : ''}`}
                                onClick={() => setGenderFilter('female')}
                            >
                                Женский
                            </div>
                            <div
                                className={`${s.selectorItem} ${genderFilter === 'unisex' ? s.active : ''}`}
                                onClick={() => setGenderFilter('unisex')}
                            >
                                Унисекс
                            </div>
                        </div>
                    </div>
                    <div className={s.archiveSelector}>
                        <div className={s.selector}>
                            <div
                                className={`${s.selectorItem} ${!isArchive ? s.active : ''}`}
                                onClick={() => handleArchiveToggle(false)}
                            >
                                Список
                            </div>
                            <div
                                className={`${s.selectorItem} ${isArchive ? s.active : ''}`}
                                onClick={() => handleArchiveToggle(true)}
                            >
                                Архив
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className={s.tabsContainer}>
                {TABS.map((tab) => (
                    <button
                        key={tab.key}
                        className={`${s.tab} ${activeTab === tab.key ? s.tabActive : ''}`}
                        onClick={() => handleTabChange(tab.key)}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            <form className={s.clothesFilterForm}
                  onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                          find(e);
                      }
                      if (e.key === 'Escape') {
                          cleanForm();
                      }
                  }}
            >
                <input value={name}
                       onChange={e => setName(e.target.value)}
                       placeholder="Введите название одежды для фильтрации"
                       className={s.filterClothesFormUp}
                />

                <div className={s.filterClothesFormUp}>
                    <CustomSelector
                        selectedItem={selectedLayer}
                        onSelectItem={setSelectedLayer}
                        availableItems={LAYERS.map(layer => layer.localizedName)}
                        typeOfDowndrop="слой"
                    />
                </div>

                <div className={s.filterClothesFormUp}>
                    <CustomSelector
                        selectedItem={manufacturer}
                        onSelectItem={setManufacturer}
                        availableItems={brands}
                        typeOfDowndrop="производителя"
                    />
                </div>

                <input value={id}
                       onChange={e => setId(e.target.value)}
                       placeholder="Введите Id для фильтрации"
                       className={s.filterClothesFormUp}
                />
                <input value={vendorCode}
                       onChange={e => setVendorCode(e.target.value)}
                       placeholder="Введите артикул для фильтрации"
                       className={s.filterClothesFormUp}
                />
                <div className={s.characteristicsContainer}>
                    {characteristics.map((char, index) => (
                        <div key={index} className={s.characteristicGroup}>
                            <input
                                type="text"
                                placeholder="Введите характеристику одежды для фильтрации"
                                value={char.charName}
                                onChange={(e) => charNameChange(index, e.target.value)}
                                className={s.filterClothesFormDown}
                            />
                            <input
                                type="text"
                                placeholder="Введите значения характеристики одежды для фильтрации"
                                value={char.charValues || ""}
                                onChange={(e) => charValuesChange(index, e.target.value)}
                                className={s.filterClothesFormDown}
                            />

                            {index === characteristics.length - 1 && (
                                <Button type="button" className={s.addFormButton} onClick={addCharacteristic}>+</Button>
                            )}
                        </div>
                    ))}
                </div>

                <div className={s.btnClass}>
                    <div className={s.filtersBySourceTagsSelector}>
                        {!segmentedClothes && (
                            <div className={s.selector}>
                            <div
                                className={`${s.selectorItem} ${(!onlyAutoTagged && !onlyNotTagged) ? s.active : ''}`}
                                onClick={() => handleFiltersBySourceTagsToggle("all")}
                            >
                                Все
                            </div>
                            <div
                                className={`${s.selectorItem} ${onlyAutoTagged ? s.active : ''}`}
                                onClick={() => handleFiltersBySourceTagsToggle("auto")}
                            >
                                Автоматически протегированные
                            </div>
                            <div
                                className={`${s.selectorItem} ${onlyNotTagged ? s.active : ''}`}
                                onClick={() => handleFiltersBySourceTagsToggle("not")}
                            >
                                Не протегированные
                            </div>
                        </div>)}

                    </div>
                    <div className={s.btnRight}>
                        <div className={s.checkboxBlock}>
                            <input type="checkbox" checked={fullMatch} onChange={fullMatchChange} />
                            <span className={s.text}>Искать по полному совпадению</span>
                        </div>
                        <Button type="button" className={s.searchFormButton} onClick={find}>Поиск</Button>
                        <Button type="button" className={s.cleanFormButton} onClick={cleanForm}>Cбросить фильтр</Button>
                    </div>
                </div>
            </form>

            {isLoading && (
                <div className={s.loaderContainer}>
                    <Loader />
                </div>
            )}

            <div hidden={isLoading || !clothes || !clothes.length}>
                <div className={s.tableClothes}>
                    <div className={s.headertable}>
                        <div>id</div>
                        <div className={s.blockPhoto}>Фото</div>
                        <div onClick={() => changeSort('name')}>Название
                            {sortColumn === 'name' && (
                                <span>
                                    {sortDirection === 'asc' ? ' ↑' : sortDirection === 'desc' ? ' ↓' : ''}
                                </span>
                            )}
                        </div>
                        <div onClick={() => changeSort('manufacturer')}>Производитель
                            {sortColumn === 'manufacturer' && (
                                <span>
                                    {sortDirection === 'asc' ? ' ↑' : sortDirection === 'desc' ? ' ↓' : ''}
                                </span>
                            )}
                        </div>
                        <div>Цвет</div>
                        <div onClick={() => changeSort('vendorCode')}>Артикул
                            {sortColumn === 'vendorCode' && (
                                <span>
                                    {sortDirection === 'asc' ? ' ↑' : sortDirection === 'desc' ? ' ↓' : ''}
                                </span>
                            )} </div>

                        <div onClick={() => changeSort('cost')}>Стоимость
                            {sortColumn === 'cost' && (
                                <span>
                                    {sortDirection === 'asc' ? ' ↑' : sortDirection === 'desc' ? ' ↓' : ''}
                                </span>
                            )}
                        </div>
                        <div className={s.iconBlock}></div>
                    </div>

                    {clothes.map((cloth: IClothItem) => (
                        <ClothesItemList
                            key={cloth.id}
                            cloth={cloth}
                            deleteAction={deleteCloth}
                            archiveAction={archiveCloth}
                            isArchive={isArchive}
                            activeTab={activeTab}
                            deleteMissingAction={deleteMissingCloth}
                        />
                    ))}
                </div>
                <div className={s.pagination}>
                    <div className={s.searchText}>
                        <b>Перейти на страницу: </b>
                        <input
                            type="text"
                            className={s.searchPageInput}
                            value={currentPageInput}
                            onChange={e => setCurrentPageInput(e.target.value)}
                            onBlur={pageChange}
                        />
                    </div>
                    <PaginationControl
                        page={Number(currentPage)}
                        between={4}
                        total={allItems}
                        limit={itemPerPage}
                        changePage={(page) => {
                            paginate(page)
                        }}
                        ellipsis={1} />
                </div>
            </div>
            {!isLoading && (!clothes || !clothes.length) && (
                <div className={s.emptyContainer}>
                    <div className='emptyInfoWrapper'>
                        <div className='emptyInfo'>
                            <h1>База одежды пуста :(</h1>
                        </div>
                    </div>
                </div>

            )}
        </>
    )
}